<template>
  <div
    v-if="showMenu"
    class="w-full h-full fixed block top-0 left-0 bg-brand-background-overlay z-10"
  ></div>

  <!-- START MOBILE TOP MENU -->
  <div class="bg-white flex md:hidden flex-col">
    <!-- START VIEW APPLICATION TOP MENU -->
    <template v-if="currentRoute.includes('dashboard_view_application')">
      <template v-if="getCurrentApplication">
        <div class="flex flex-col py-3 pr-3">
          <div class="flex flex-row py-1">
            <div class="flex items-center mr-1">
              <ChevronLeftIcon
                class="text-brand-dark-gray w-8 h-8"
                @click="navigateToDashboard()"
              />
            </div>
            <div class="w-full flex flex-row justify-between">
              <div class="flex flex-col">
                <div class="flex items-center">
                  <h5>{{ getCurrentApplication.applicant.name }}</h5>
                </div>
                <p class="text-brand-dark-gray">
                  {{ getCurrentApplication.unit_address }}
                </p>
              </div>
              <div class="flex flex-row items-center">
                <template v-if="getCurrentUserContext !== 'applicant'">
                  <PaperPlaneIcon
                    :classes="'text-brand-green w-7 h-7 mr-5'"
                    @click="openShareApplicationModal"
                  />
                  <DotsVerticalIcon class="w-7 h-7 text-brand-mid-gray" @click="openMobileModal" />
                </template>
              </div>
            </div>
          </div>

          <template v-if="getCurrentUserContext !== 'applicant'">
            <div class="flex py-1 w-full pl-3">
              <template v-if="['approved', 'rejected'].includes(getCurrentApplication.status)">
                <div
                  class="flex flex-col rounded-lg border-2 px-2 bg-brand-light-gray"
                  :class="[
                    getCurrentApplication.status === 'approved'
                      ? 'border-brand-green'
                      : 'border-brand-red'
                  ]"
                >
                  <p class="py-1 font-semibold">
                    {{ getCurrentApplication.status === "approved" ? "Selected" : "Declined" }}
                    on
                    {{ formatDecisionTime(getCurrentApplication.decision_date) }}
                  </p>
                  <p class="py-1">by {{ getCurrentApplication.decision_by }}</p>
                </div>
              </template>
              <template v-else>
                <MobileDropDownMenu
                  v-if="getCurrentApplication.decisioning_enabled"
                  :app="getCurrentApplication"
                  @approve="openApproveModal"
                  @reject="openRejectModal"
                  @archive="archiveApplication"
                  @unarchive="unarchiveApplication"
                />
              </template>
            </div>
          </template>
        </div>
      </template>
    </template>
    <!-- END VIEW APPLICATION TOP MENU -->

    <!-- START DEFAULT TOP MENU -->
    <template v-else>
      <div class="flex justify-between">
        <div class="">
          <template v-if="displayBackButton">
            <button class="py-4 px-2" @click="navigateBack">
              <ChevronLeftIcon class="w-7 h-7 text-brand-green" />
            </button>
          </template>
          <template v-else>
            <button
              class="mobile-menu-button py-4 px-2 focus:outline-none focus:bg-gray-700"
              @click="toggleMenu"
            >
              <MenuIcon class="w-7 h-7 text-brand-green" />
            </button>
          </template>
        </div>

        <div class="flex items-center justify-center">
          <p class="text-lg text-brand-black font-semibold capitalize">
            {{ currentActiveLabel }}
          </p>
        </div>

        <div class="pr-3 py-2 flex items-center justify-end">
          <!-- START CREATE NEW FORM BUTTON -->
          <div v-if="currentRoute === 'dashboard_forms'" class="w-20">
            <template v-if="getApplicationTemplates && getApplicationTemplates.length > 0">
              <SecondaryButton @click="createForm">
                <div class="flex flex-row">
                  <div class="flex items-center mr-1">
                    <PlusIcon class="text-brand-green w-4 h-4" />
                  </div>
                  <div class="flex items-center">
                    <p class="text-xs font-normal">New</p>
                  </div>
                </div>
              </SecondaryButton>
            </template>
          </div>
          <!-- END CREATE NEW FORM BUTTON -->

          <!-- START SEARCH BUTTON -->
          <div
            v-if="currentRoute.includes('dashboard_applicants')"
            class="flex items-center py-1 px-1"
            @click="openSearchPanel"
            :class="[
              getApplicationBundleSearchTerm ? 'rounded bg-brand-background-light-green' : ''
            ]"
          >
            <SearchIcon class="w-6 h-6 text-brand-green" />
          </div>
        </div>
      </div>

      <div class="flex flex-row justify-between items-center px-2 pb-3" v-if="showTabs">
        <div class="flex flex-row">
          <template v-for="tab in tabs" :key="tab.route">
            <router-link
              :to="{
                name: tab.route,
                params: { slug: $route.params.slug }
              }"
            >
              <StyledTab :active="currentRoute === tab.route">
                {{ tab.label }}
              </StyledTab>
            </router-link>
          </template>
        </div>
        <div class="flex flex-row">
          <TrashIcon
            v-if="getCurrentApplicationTemplate && getCurrentApplicationTemplate.can_edit"
            class="text-brand-red w-6 h-6 mr-2 cursor-pointer"
            @click="toggleDeleteFormModal"
          />
          <div
            v-if="currentRoute === 'dashboard_form_edit'"
            class="flex md:hidden items-center py-1 px-1 bg-brand-green rounded mr-5 w-7 h-7"
            @click="openFormEditMobileSideMenu()"
          >
            <PlusIcon class="w-5 h-5 text-white" />
          </div>
        </div>
      </div>
    </template>
    <!-- END DEFAULT TOP MENU -->
  </div>
  <!-- END MOBILE TOP MENU -->

  <!-- START MOBILE SEARCH PANEL -->
  <div
    class="bg-white w-full py-3 absolute inset-y-0 left-0 transform transition duration-200 ease-in-out z-20"
    v-if="showSearchPanel"
  >
    <div class="flex flex-col">
      <SearchInput @search="handleSearch" :is-main-menu="true" />
    </div>
  </div>

  <!-- START DESKTOP SIDE MENU -->
  <div
    v-if="showDesktopMenu"
    class="sidebar bg-white shadow-md w-20 py-5 md:py-8 inset-y-0 left-0 transform transition-all md:relative md:translate-x-0 ease-in-out z-20 h-screen fixed"
    :class="[
      isMobile ? 'duration-300' : 'duration-75',
      expanded ? 'w-52' : '',
      isMobile ? (showMenu ? 'translate-x-0' : '-translate-x-full') : ''
    ]"
    @mouseover="expandMenu"
    @mouseleave="shrinkMenu"
    ref="menu"
  >
    <!-- logo -->
    <div class="flex flex-col justify-between items-stretch h-full">
      <div class="flex flex-col">
        <router-link
          v-if="!showMenu"
          :to="{
            name:
              getCurrentUserContext === 'landlord'
                ? 'dashboard_applicants_submitted'
                : 'dashboard_applicants_submitted'
          }"
          class="text-white flex items-center space-x-2 px-4"
        >
          <img class="w-10 ml-1" src="@/assets/images/main-logo.svg" />
        </router-link>

        <div v-if="showMenu" @click="toggleMenu" class="flex items-center px-8">
          <XIcon class="text-brand-green w-8 h-8" />
        </div>

        <!-- nav -->
        <template v-if="getCurrentUserContext === 'landlord'">
          <nav class="py-5 md:py-10">
            <router-link
              tag="div"
              :to="{ name: 'dashboard_applicants_submitted' }"
              class="py-4 px-4 transition duration-200 hover:bg-brand-light-gray text-brand-gray flex"
              :class="{
                'border-r-4 border-brand-green bg-brand-light-gray': currentActive === 'applicants'
              }"
              @click="closeMenu"
            >
              <div class="flex items-center ml-3">
                <UserCircleIcon
                  class="w-7 h-7"
                  :class="[currentActive === 'applicants' ? 'text-brand-green' : 'text-brand-gray']"
                />
              </div>

              <div class="flex items-center" v-if="expanded">
                <p
                  class="ml-5 text-sm"
                  :class="[
                    currentActive === 'applicants' ? 'text-brand-green' : 'text-brand-dark-gray'
                  ]"
                >
                  Applicants
                </p>
              </div>
            </router-link>

            <router-link
              tag="div"
              :to="{ name: 'dashboard_forms', query: { reload: true } }"
              class="py-4 px-4 transition duration-200 hover:bg-brand-light-gray flex"
              :class="{
                'border-r-4 border-brand-green bg-brand-light-gray': currentActive === 'forms'
              }"
              @click="closeMenu"
            >
              <div class="flex items-center ml-3">
                <DocumentTextIcon
                  class="w-7 h-7"
                  :class="[currentActive === 'forms' ? 'text-brand-green' : 'text-brand-gray']"
                />
              </div>
              <div class="flex items-center" v-if="expanded">
                <p
                  class="ml-5 text-sm"
                  :class="[currentActive === 'forms' ? 'text-brand-green' : 'text-brand-dark-gray']"
                >
                  Forms
                </p>
              </div>
            </router-link>

            <router-link
              tag="div"
              :to="{ name: 'dashboard_documents', query: { reload: true } }"
              class="py-4 px-4 transition duration-200 hover:bg-brand-light-gray flex"
              :class="{
                'border-r-4 border-brand-green bg-brand-light-gray': currentActive === 'documents'
              }"
              @click="closeMenu"
              v-if="getCurrentUser.landlord.beta_features_enabled"
            >
              <div class="flex items-center ml-3">
                <svg
                  class="w-7 h-7"
                  :class="[currentActive === 'documents' ? 'text-brand-green' : 'text-brand-gray']"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0118 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3l1.5 1.5 3-3.75"
                  />
                </svg>
              </div>
              <div class="flex items-center" v-if="expanded">
                <p
                  class="ml-5 text-sm"
                  :class="[
                    currentActive === 'documents' ? 'text-brand-green' : 'text-brand-dark-gray'
                  ]"
                >
                  Documents
                </p>
              </div>
            </router-link>

            <router-link
              v-if="getCurrentUser && getCurrentUser.landlord.show_my_payments"
              tag="div"
              :to="{ name: 'dashboard_my_payments' }"
              class="py-4 px-4 transition duration-200 hover:bg-brand-light-gray flex"
              :class="[
                currentActive === 'my_payments'
                  ? 'border-r-4 border-brand-green bg-brand-light-gray'
                  : ''
              ]"
              @click="closeMenu"
            >
              <div class="flex items-center ml-3">
                <CurrencyDollarIcon
                  class="w-7 h-7"
                  :class="[
                    currentActive === 'my_payments' ? 'text-brand-green' : 'text-brand-gray'
                  ]"
                />
              </div>
              <div class="flex items-center" v-if="expanded">
                <p
                  class="ml-5 text-sm whitespace-nowrap"
                  :class="[
                    currentActive === 'my_payments' ? 'text-brand-green' : 'text-brand-gray'
                  ]"
                >
                  My Payments
                </p>
              </div>
            </router-link>

            <router-link
              tag="div"
              :to="{ name: 'dashboard_teams' }"
              class="py-4 px-4 transition duration-200 hover:bg-brand-light-gray flex"
              :class="[
                currentActive === 'teams' ? 'border-r-4 border-brand-green bg-brand-light-gray' : ''
              ]"
              @click="closeMenu"
              v-if="getCurrentUser.can_admin_company"
            >
              <div class="flex items-center ml-3">
                <UserGroupIcon
                  class="w-7 h-7"
                  :class="[currentActive === 'teams' ? 'text-brand-green' : 'text-brand-gray']"
                />
              </div>
              <div class="flex items-center" v-if="expanded">
                <p
                  class="ml-5 text-sm"
                  :class="[currentActive === 'teams' ? 'text-brand-green' : 'text-brand-gray']"
                >
                  Teams
                </p>
              </div>
            </router-link>

            <router-link
              tag="div"
              :to="{ name: 'dashboard_settings' }"
              class="py-4 px-4 transition duration-200 hover:bg-brand-light-gray flex"
              :class="[
                currentActive === 'settings'
                  ? 'border-r-4 border-brand-green bg-brand-light-gray'
                  : ''
              ]"
              @click="closeMenu"
            >
              <div class="flex items-center ml-3">
                <CogIcon
                  class="w-7 h-7"
                  :class="[currentActive === 'settings' ? 'text-brand-green' : 'text-brand-gray']"
                />
              </div>
              <div class="flex items-center" v-if="expanded">
                <p
                  class="ml-5 text-sm"
                  :class="[currentActive === 'settings' ? 'text-brand-green' : 'text-brand-gray']"
                >
                  Settings
                </p>
              </div>
            </router-link>

            <div
              class="py-4 px-4 transition duration-200 flex cursor-pointer"
              @click="openNotifications"
            >
              <div
                class="flex ml-3"
                :class="[
                  !expanded ? 'items-center justify-center' : '',
                  currentActive === 'notifications' || showNotificationPanel
                    ? 'rounded bg-brand-light-gray'
                    : ''
                ]"
              >
                <BellIcon
                  class="w-7 h-7"
                  :class="[
                    currentActive === 'notifications' || showNotificationPanel
                      ? 'text-brand-green'
                      : 'text-brand-dark-gray'
                  ]"
                />

                <div class="flex items-center" v-if="expanded">
                  <p
                    class="ml-4 text-sm"
                    :class="[
                      currentActive === 'notifications' || showNotificationPanel
                        ? 'text-brand-green'
                        : 'text-brand-dark-gray'
                    ]"
                  >
                    Notifications
                  </p>
                </div>
                <div class="flex h-full items-start" v-if="getNotificationUnreadCount > 0">
                  <AlertDot />
                </div>
              </div>
            </div>
          </nav>
        </template>

        <template v-else>
          <nav class="py-5 md:py-10">
            <router-link
              tag="div"
              :to="{ name: 'applicant_dashboard_applications' }"
              class="py-4 px-4 transition duration-200 hover:bg-brand-light-gray text-brand-dark-gray flex"
              :class="{
                'px-8': expanded,
                'border-r-4 border-brand-green bg-brand-light-gray':
                  currentActive === 'applications'
              }"
              @click="closeMenu"
            >
              <UserCircleIcon
                class="w-7 h-7"
                :class="{
                  'mx-auto': !expanded,
                  'text-brand-green': currentActive === 'applications',
                  'text-brand-dark-gray': currentActive !== 'applications'
                }"
              />

              <div class="flex items-center" v-if="expanded">
                <p
                  class="ml-5 text-sm"
                  :class="[
                    currentActive === 'applications' ? 'text-brand-green' : 'text-brand-dark-gray'
                  ]"
                >
                  Applications
                </p>
              </div>
            </router-link>
            <router-link
              tag="div"
              :to="{ name: 'dashboard_settings' }"
              class="py-4 px-4 transition duration-200 hover:bg-brand-light-gray flex"
              :class="{
                'px-8': expanded,
                'border-r-4 border-brand-green bg-brand-light-gray': currentActive === 'settings'
              }"
              @click="closeMenu"
            >
              <CogIcon
                class="w-7 h-7"
                :class="{
                  'mx-auto': !expanded,
                  'text-brand-green': currentActive === 'settings',
                  'text-brand-dark-gray': currentActive !== 'settings'
                }"
              />
              <div class="flex items-center" v-if="expanded">
                <p
                  class="ml-5 text-sm"
                  :class="[
                    currentActive === 'settings' ? 'text-brand-green' : 'text-brand-dark-gray'
                  ]"
                >
                  Settings
                </p>
              </div>
            </router-link>
          </nav>
        </template>
      </div>

      <div class="flex flex-col">
        <div
          v-if="getCurrentUser && getCurrentUser.landlord"
          class="py-4 px-4 transition duration-200 flex"
        >
          <div class="flex items-center ml-1">
            <Gravatar
              :photo="getCurrentUser.landlord.profile_photo"
              :displayName="gravatarDisplayName"
              :color="`bg-palette-gravatar-1`"
              :displayCutout="false"
              :zIndex="1"
            />
          </div>
          <div class="flex flex-col justify-center w-36" v-show="expanded">
            <p class="ml-2 font-semibold truncate">
              {{ getCurrentUser.landlord.name || "Name not provided" }}
            </p>
            <p
              class="ml-2 text-sm truncate"
              :class="[currentActive === 'teams' ? 'text-brand-green' : 'text-brand-gray']"
            >
              {{
                getCurrentUser.username.length > 20
                  ? `${getCurrentUser.username.substring(0, 20)}...`
                  : getCurrentUser.username
              }}
            </p>
          </div>
        </div>

        <div
          class="py-4 px-4 transition duration-200 hover:bg-brand-light-gray text-brand-dark-gray flex cursor-pointer"
          @click="switchContext"
          v-if="getCurrentUser && getCurrentUser.landlord && getCurrentUser.applicant"
        >
          <div class="flex items-center ml-3">
            <img src="@/assets/icons/switch.svg" class="w-7 h-7" />
          </div>

          <div class="flex items-center" v-if="expanded">
            <p class="ml-5 text-sm text-brand-dark-gray">
              View as
              {{ getCurrentUserContext === "landlord" ? "applicant" : "landlord" }}
            </p>
          </div>
        </div>

        <div
          class="py-4 px-4 transition duration-200 hover:bg-brand-light-gray text-brand-dark-gray flex cursor-pointer"
          :class="{
            'px-8': expanded
          }"
          @click="logout"
        >
          <LogoutIcon
            class="w-7 h-7"
            :class="{
              'mx-auto': !expanded
            }"
          />

          <div class="flex items-center" v-if="expanded">
            <p class="ml-5 text-sm text-brand-dark-gray">Log out</p>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- END DESKTOP SIDE MENU -->

  <template v-if="getCurrentApplication">
    <MobileAdditionalActionModal
      :showViewAppLink="false"
      :showModal="showMobileModal"
      :app="getCurrentApplication"
      @close="closeMobileModal"
      @history="openHistoryModal"
      @notes="openNotesModal"
      @share="openShareApplicationModal"
    />
  </template>

  <ActionToast ref="mainMenuToast" />

  <NotificationMenu :showMenu="showNotificationPanel" @close="closeNotifications" />
  <DeleteFormModal :showModal="showDeleteFormModal" @close="toggleDeleteFormModal" />
</template>

<script>
import moment from "moment"

import {
  APPLICATION_GET,
  APPLICATION_BUNDLE_GET_LIST,
  AUTH_LOGOUT,
  NOTIFICATION_GET_LIST
} from "@/store/types"

import {
  UserCircleIcon,
  CogIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  XIcon,
  MenuIcon,
  PlusIcon,
  ChevronLeftIcon,
  SearchIcon,
  DotsVerticalIcon,
  UserGroupIcon,
  BellIcon,
  LogoutIcon,
  TrashIcon
} from "@heroicons/vue/outline"

import MobileDropDownMenu from "@/views/dashboard/view_application/components/MobileDropDownMenu.vue"
import MobileAdditionalActionModal from "@/views/dashboard/view_application/components/MobileAdditionalActionModal.vue"

import NotificationMenu from "@/views/dashboard/notifications/NotificationMenu.vue"
import DeleteFormModal from "@/views/dashboard/form/components/DeleteFormModal.vue"

import { mapGetters } from "vuex"
import {
  USER_SET_CONTEXT,
  APPLICATION_BUNDLE_ARCHIVE,
  APPLICATION_BUNDLE_UNARCHIVE
} from "@/store/types"

export default {
  components: {
    UserCircleIcon,
    CogIcon,
    DocumentTextIcon,
    CurrencyDollarIcon,
    XIcon,
    MenuIcon,
    PlusIcon,
    DotsVerticalIcon,
    UserGroupIcon,
    BellIcon,
    ChevronLeftIcon,
    SearchIcon,
    LogoutIcon,
    MobileDropDownMenu,
    MobileAdditionalActionModal,
    NotificationMenu,
    DeleteFormModal,
    TrashIcon
  },
  watch: {
    $route: {
      // eslint-disable-next-line no-unused-vars
      handler: function (to, from) {
        this.displayBackButton = to.matched.some((route) => route.meta.displayBackButton)
        this.showTabs = to.matched.some((route) => route.meta.showTabs)

        if (this.showTabs) {
          this.tabs = to.matched
            .map((route) => {
              if (route.meta.showTabs) {
                return route.meta.tabs
              }
            })
            .flat()
            .filter(Boolean)
        } else {
          this.tabs = []
        }
        if (this.showNotificationPanel) {
          this.showNotificationPanel = false
        }
      },
      deep: true,
      immediate: true
    }
  },
  computed: {
    ...mapGetters([
      "getCurrentApplicationTemplate",
      "isApplicationBundleLoading",
      "getApplicationBundleSearchTerm",
      "getApplicationTemplates",
      "getCurrentApplication",
      "getCurrentUser",
      "getCurrentUserContext",
      "getNotifications",
      "getNotificationUnreadCount"
    ]),
    showDesktopMenu() {
      if (this.$route.query.iframe) {
        return false
      }

      return true
    },
    currentActiveLabel() {
      if (["dashboard_form_settings", "dashboard_form_edit"].includes(this.currentRoute)) {
        return this.getCurrentApplicationTemplate ? this.getCurrentApplicationTemplate.name : ""
      } else if (this.currentRoute === "dashboard_notifications") {
        return "Notification Center"
      } else if (this.currentRoute === "dashboard_forms_flyer") {
        return "Listing Handouts"
      } else {
        return this.currentActive.split("_").join(" ")
      }
    },
    currentActive() {
      if (this.currentRoute.includes("form")) {
        return "forms"
      }

      if (this.currentRoute.includes("documents")) {
        return "documents"
      }

      if (this.currentRoute === "applicant_dashboard_applications") {
        return "applications"
      }

      if (
        this.currentRoute.includes("applicants") ||
        this.currentRoute.includes("view_application")
      ) {
        return "applicants"
      }

      if (this.currentRoute.includes("ambassador")) {
        return "ambassador"
      }

      if (this.currentRoute.includes("my_payments")) {
        return "my_payments"
      }

      if (this.currentRoute.includes("settings")) {
        return "settings"
      }

      if (this.currentRoute.includes("teams")) {
        return "teams"
      }

      if (this.currentRoute.includes("notifications")) {
        return "notifications"
      }
      return ""
    },
    currentRoute() {
      return this.$route.name
    },
    gravatarDisplayName() {
      if (!this.getCurrentUser || !this.getCurrentUser.landlord) {
        return ""
      }

      const landlordName = this.getCurrentUser.landlord.name || this.getCurrentUser.username
      if (landlordName.split(" ").length > 1) {
        return `${landlordName.split(" ")[0][0]}${landlordName.split(" ")[1][0]}`
      } else {
        return `${landlordName.substring(0, 2)}`
      }
    }
  },
  async mounted() {
    if (!this.getNotifications) {
      try {
        await this.$store.dispatch(NOTIFICATION_GET_LIST)
      } catch (err) {
        console.log(err)
      }
    }
  },
  data() {
    return {
      expanded: false,
      showMenu: false,
      displayBackButton: false,
      showTabs: false,
      tabs: null,
      showSearchPanel: false,
      showMobileModal: false,
      showNotificationPanel: false,
      showDeleteFormModal: false
    }
  },
  methods: {
    toggleDeleteFormModal() {
      this.showDeleteFormModal = !this.showDeleteFormModal
    },
    openNotifications() {
      if (!this.isMobile) {
        this.shrinkMenu()
        this.showNotificationPanel = true
      } else {
        this.$router.push({ name: "dashboard_notifications" })
        this.closeMenu()
      }
    },
    closeNotifications() {
      this.showNotificationPanel = false
    },
    openFormEditMobileSideMenu() {
      this.$emitter.emit("dashboard_form_edit_mobile_side_menu", "open")
    },
    openApproveModal() {
      this.$emitter.emit("dashboard_view_application_approve_modal", "open")

      if (this.showMobileModal) this.closeMobileModal()
    },
    openRejectModal() {
      this.$emitter.emit("dashboard_view_application_reject_modal", "open")

      if (this.showMobileModal) this.closeMobileModal()
    },
    async archiveApplication() {
      const slugs = [this.getCurrentApplication.bundle.slug]

      await this.$store.dispatch(APPLICATION_BUNDLE_ARCHIVE, { slugs })

      this.$refs.mainMenuToast.show({
        type: "success",
        hasClose: true,
        content: "Applicant archived",
        icon: "check"
      })

      await this.loadApplication()
    },
    async unarchiveApplication() {
      const slugs = [this.getCurrentApplication.bundle.slug]

      await this.$store.dispatch(APPLICATION_BUNDLE_UNARCHIVE, { slugs })
      this.$refs.mainMenuToast.show({
        type: "success",
        hasClose: true,
        content: "Applicant unarchived",
        icon: "check"
      })

      await this.loadApplication()
    },
    openNotesModal() {
      this.$emitter.emit("dashboard_view_application_notes_modal", "open")
      if (this.showMobileModal) this.closeMobileModal()
    },
    openHistoryModal() {
      this.$emitter.emit("dashboard_view_application_application_history_modal", "open")
      if (this.showMobileModal) this.closeMobileModal()
    },
    openShareApplicationModal() {
      this.$emitter.emit("dashboard_view_application_share_application_modal", "open")
      if (this.showMobileModal) this.closeMobileModal()
    },

    formatDecisionTime(date) {
      return moment(date).format("MM/DD/YY @ h:mma")
    },
    async loadApplication() {
      try {
        if (this.$route.name.includes("dashboard_applicants")) {
          const status = this.$route.name.split("dashboard_applicants_")[1]
          await this.$store.dispatch(APPLICATION_BUNDLE_GET_LIST, {
            status
          })
        } else {
          const id = this.$route.params.id
          await this.$store.dispatch(APPLICATION_GET, { id })
        }
      } catch (err) {
        console.log(err)
      }
    },
    async handleSearch(val) {
      const status = this.$route.name.split("dashboard_applicants_")[1]
      try {
        await this.$store.dispatch(APPLICATION_BUNDLE_GET_LIST, {
          status,
          search: val
        })
        this.showSearchPanel = false
      } catch (err) {
        console.log(err)
      }
    },
    expandMenu() {
      this.expanded = true
    },
    shrinkMenu() {
      this.expanded = false
    },
    closeMenu() {
      this.showMenu = false
    },
    toggleMenu() {
      const that = this
      setTimeout(() => {
        that.showMenu = !that.showMenu
        that.expanded = true
      }, 10)

      if (this.showMenu) {
        this.$emit("menu-open")
      } else {
        this.$emit("menu-close")
      }
    },
    navigateBack() {
      if (this.currentRoute.includes("form")) {
        this.$router.push({ name: "dashboard_forms" })
      }
    },
    navigateToDashboard() {
      if (this.getCurrentUserContext === "applicant") {
        this.$router.push({ name: "applicant_dashboard_applications" })
      } else {
        this.$router.push({ name: "dashboard_applicants" })
      }
    },
    openSearchPanel() {
      this.showSearchPanel = true
    },
    openMobileModal() {
      this.showMobileModal = true
    },
    closeMobileModal() {
      this.showMobileModal = false
    },
    createForm() {
      this.$router.push({ name: "dashboard_new_form_reports" })
    },
    switchContext() {
      const newContext = this.getCurrentUserContext === "landlord" ? "applicant" : "landlord"
      this.$store.commit(USER_SET_CONTEXT, newContext)
      if (newContext === "applicant") {
        this.$router.push({ name: "applicant_dashboard_applications" })
      } else {
        this.$router.push({ name: "dashboard_applicants" })
      }
      this.closeMenu()
    },
    async logout() {
      await this.$store.dispatch(AUTH_LOGOUT, true)
      this.$store.reset()
      this.$router.push({ name: "home_landing" })
    }
  }
}
</script>
